import { createNavigationItem, createNavigationItemList, configureExternalNavigationItems, navigationItemTypes } from '@/Utils/NavigationUtility';
import { userNavigationGroupTypes } from '@/Types/UserNavigationGroupTypes';
import Config from '@/Config';

/**
 * Builds user navigation groups for the user box dropdown menu
 * @param {Object} options - Configuration options
 * @param {Object} options.userContext - User context from store
 * @param {Object} options.projectContext - Project context from store
 * @param {Object} options.resources - Translation resources
 * @param {Object} options.displayMode - Display mode (web/mobile/PWA)
 * @param {boolean} options.isAdminMode - Whether user is in admin mode
 * @param {Function} options.toggleTestingModeAction - Toggle test mode action
 * @param {Function} options.logoutAction - Logout action
 * @returns {Object} Grouped navigation links
 */
export function buildUserNavigationGroups(options) {
    const {
        userContext,
        projectContext,
        resources,
        displayMode,
        isAdminMode,
        toggleTestingModeAction,
        logoutAction,
    } = options;

    const testModeActionText = projectContext.isInTestMode
        ? resources.Layout.UserBoxDisableTestModeButton
        : resources.Layout.UserBoxEnableTestModeButton;

    const userProfileUrl = Config.navigation.userProfileUrl;

    const links = createNavigationItemList([
        createNavigationItem({
            key: 'OpenAdmin',
            label: resources.Layout.UserBoxAdminLink,
            iconName: 'tools',
            to: Config.navigation.adminUrl,
            navigationItemType: navigationItemTypes.external,
            isEnabled: Config.currentUser.hasAccessToAdminMode && !isAdminMode && displayMode.isWeb,
            groupType: userNavigationGroupTypes.adminLinks,
        }),
        createNavigationItem({
            key: 'OpenGamefound',
            label: resources.Layout.UserBoxPlatformLink,
            icon: {
                name: 'brand-outline',
                namespace: 'ga',
            },
            to: Config.navigation.homeUrl,
            navigationItemType: navigationItemTypes.external,
            isEnabled: isAdminMode,
            groupType: userNavigationGroupTypes.adminLinks,
        }),
        createNavigationItem({
            label: testModeActionText,
            iconName: 'sliders-h',
            isEnabled: projectContext.canEnableTestMode,
            navigationItemType: navigationItemTypes.action,
            action: toggleTestingModeAction,
            groupType: userNavigationGroupTypes.testerLinks,
        }),
        createNavigationItem({
            label: resources.Layout.UserBoxYourDashboardLink,
            iconName: 'rectangles-mixed',
            to: Config.navigation.backerCenterDashboardUrl,
            navigationItemType: navigationItemTypes.link,
            isEnabled: !isAdminMode && !projectContext.isInTestMode,
            groupType: userNavigationGroupTypes.backerCenterLinks,
        }),
        createNavigationItem({
            label: resources.Layout.UserBoxPledgesAndOrdersLink,
            iconName: 'receipt',
            to: Config.navigation.backerCenterPledgesAndOrdersUrl,
            navigationItemType: navigationItemTypes.link,
            isEnabled: !isAdminMode && !projectContext.isInTestMode,
            groupType: userNavigationGroupTypes.backerCenterLinks,
        }),
        createNavigationItem({
            label: resources.Layout.UserBoxPaymentsLink,
            iconName: 'money-bill-wave',
            to: Config.navigation.backerCenterPaymentsUrl,
            navigationItemType: navigationItemTypes.link,
            isEnabled: !isAdminMode && !projectContext.isInTestMode,
            groupType: userNavigationGroupTypes.backerCenterLinks,
        }),
        createNavigationItem({
            label: resources.Layout.UserBoxProjectsLink,
            iconName: 'heart',
            to: Config.navigation.backerCenterYourProjectsUrl,
            navigationItemType: navigationItemTypes.link,
            isEnabled: !isAdminMode && !projectContext.isInTestMode,
            groupType: userNavigationGroupTypes.backerCenterLinks,
        }),
        createNavigationItem({
            label: resources.Layout.UserBoxGiftCardsLink,
            iconName: 'gift-card',
            to: Config.navigation.backerCenterGiftCardsUrl,
            navigationItemType: navigationItemTypes.link,
            isEnabled: !isAdminMode && !projectContext.isInTestMode,
            groupType: userNavigationGroupTypes.backerCenterLinks,
        }),
        createNavigationItem({
            label: resources.Layout.UserBoxUpdatesFeedLink,
            iconName: 'megaphone',
            to: Config.navigation.backerCenterUpdatesUrl,
            navigationItemType: navigationItemTypes.link,
            isEnabled: !isAdminMode && !projectContext.isInTestMode,
            groupType: userNavigationGroupTypes.backerCenterLinks,
        }),
        createNavigationItem({
            label: resources.Layout.UserBoxMyAccountLink,
            iconName: 'cog',
            to: Config.navigation.accountSettingsUrl,
            isEnabled: !isAdminMode,
            navigationItemType: navigationItemTypes.link,
            groupType: userNavigationGroupTypes.accountSettingsLinks,
        }),
        createNavigationItem({
            label: resources.Layout.UserBoxCommunicationPreferencesLink,
            iconName: 'list-check',
            to: Config.navigation.subscriptionManagementUrl,
            isEnabled: !isAdminMode,
            navigationItemType: navigationItemTypes.link,
            groupType: userNavigationGroupTypes.accountSettingsLinks,
        }),
        createNavigationItem({
            label: resources.Layout.UserBoxProfileLink,
            iconName: 'user',
            to: userProfileUrl,
            navigationItemType: navigationItemTypes.link,
            isEnabled: userProfileUrl && !isAdminMode,
            groupType: userNavigationGroupTypes.profileLinks,
        }),
        createNavigationItem({
            label: resources.Layout.UserBoxLogoutButton,
            iconName: 'sign-out-alt',
            isEnabled: !userContext.isImpersonated,
            navigationItemType: navigationItemTypes.action,
            action: logoutAction,
            groupType: userNavigationGroupTypes.logoutLinks,
        }),
    ]).map(configureExternalNavigationItems);

    return links.reduce((acc, link) => {
        if (!acc[link.groupType]) {
            acc[link.groupType] = [];
        }
        acc[link.groupType].push(link);
        return acc;
    }, {});
}